import { Head, router, usePage } from '@inertiajs/react'
import type { ReactNode } from 'react'
import PersistentLayout from '@/layouts/PersistentLayout'
import DashboardLayout, { type DashboardSharedProps } from '@/layouts/DashboardLayout'
import { GroupCard, GroupHeading } from '@/components/dashboard/GroupCard'

type DoctorFollowUp = {
  id: number
  doctor_name: string | null
  event_label: string
  title: string
  elapsed_label: string
  link_path: string
}

type CsExamination = {
  id: number
  start_at: string
  patient_name: string | null
  doctor_name: string | null
  department_name: string | null
  status_text: string
  insurance_card_missing: boolean
  path: string
}

type UnassignedConsultation = {
  id: number
  patient_name: string | null
  elapsed_label: string
  path: string
}

type ReplyAwaitingConsultation = {
  id: number
  consultation_id: number
  patient_name: string | null
  doctor_name: string | null
  last_message_at: string
  elapsed_label: string
  path: string
}

type SlotShortage = {
  date_label: string
  time_range: string
  missing: number
  calendar_path: string
}

type PrecheckExamination = CsExamination & {
  due_soon: boolean
  contact_issues: string[]
  pharmacy_name: string | null
  pharmacy_distance_km: number | null
  pharmacy_far: boolean
  precheck_path: string
}

type RecentPrecheck = {
  id: number
  patient_name: string | null
  checked_by: string
  checked_at: string
  undo_path: string
}

type CsHandover = {
  id: number
  patient_name: string | null
  doctor_name: string | null
  memo: string
  created_label: string
  elapsed_label: string
  path: string
  resolve_path: string
}

type CsHomeProps = DashboardSharedProps & {
  doctor_follow_ups: DoctorFollowUp[]
  doctor_follow_up_total: number
  confirm_all_follow_ups_path: string
  pending_examinations: CsExamination[]
  today_examinations: CsExamination[]
  slot_shortages: SlotShortage[]
  unassigned_consultations: UnassignedConsultation[]
  reply_awaiting_consultations: ReplyAwaitingConsultation[]
  precheck_pending_examinations: PrecheckExamination[]
  recent_prechecks: RecentPrecheck[]
  cs_handovers: CsHandover[]
}

const listClass = 'divide-y divide-gray-100 bg-white border border-gray-200 rounded-lg max-h-96 overflow-y-auto'

/** 今日の診療を開始時刻ごとにまとめる（同時刻の診療を同じ行に横並びで出すため） */
function groupByStartTime(examinations: CsExamination[]): { time: string; examinations: CsExamination[] }[] {
  const groups: { time: string; examinations: CsExamination[] }[] = []
  for (const e of examinations) {
    const last = groups[groups.length - 1]
    if (last && last.time === e.start_at) {
      last.examinations.push(e)
    } else {
      groups.push({ time: e.start_at, examinations: [e] })
    }
  }
  return groups
}

function ElapsedBadge({ label }: { label: string }) {
  return (
    <span className="text-[11px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 whitespace-nowrap">
      {label}
    </span>
  )
}

/** 要対応グループ内の小見出しつきリスト */
function ActionBlock({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-red-600 mb-1">{title}</h3>
      <p className="text-xs text-gray-500 mb-2">{description}</p>
      {children}
    </div>
  )
}

export default function CsHome() {
  const {
    doctor_follow_ups, doctor_follow_up_total, confirm_all_follow_ups_path,
    pending_examinations, today_examinations, slot_shortages,
    unassigned_consultations, reply_awaiting_consultations, nav_paths,
    precheck_pending_examinations, recent_prechecks, cs_handovers,
  } = usePage().props as unknown as CsHomeProps

  const markPrechecked = (e: PrecheckExamination) => {
    router.post(e.precheck_path, {}, { preserveScroll: true })
  }
  const undoPrecheck = (p: RecentPrecheck) => {
    router.delete(p.undo_path, { preserveScroll: true })
  }
  const resolveHandover = (h: CsHandover) => {
    router.patch(h.resolve_path, {}, { preserveScroll: true })
  }
  // 表示中の20件だけでなく、裏に溜まっている未確認の全件をまとめて対応済みにする (#1283)
  const confirmAllFollowUps = () => {
    if (!window.confirm(`${doctor_follow_up_total}件を対応済みにします。よろしいですか？`)) return
    router.post(confirm_all_follow_ups_path, {}, { preserveScroll: true })
  }

  const hasActionItems = doctor_follow_ups.length > 0 || pending_examinations.length > 0 ||
    unassigned_consultations.length > 0 || reply_awaiting_consultations.length > 0
  const insuranceMissingCount = today_examinations.filter((e) => e.insurance_card_missing).length

  return (
    <>
      <Head title="ダッシュボード" />

      <GroupCard header={<GroupHeading title="要対応" />} tone="attention">
        {!hasActionItems && (
          <p className="text-sm text-gray-500">現在対応が必要な項目はありません。</p>
        )}
        <div className="grid gap-6 lg:grid-cols-2 items-start">
          {doctor_follow_ups.length > 0 && (
            <ActionBlock
              title="重要通知を開いていない医師"
              description="キャンセル・担当変更・対応依頼の通知を医師が開いていません。連絡してフォローしてください。医師が通知を開くと消えます。"
            >
              <ul className={listClass}>
                {doctor_follow_ups.map((f) => (
                  <li key={f.id}>
                    <a href={f.link_path} target="_blank" rel="noopener" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                      <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0">{f.doctor_name} 医師</span>
                      <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 whitespace-nowrap shrink-0">{f.event_label}</span>
                      <span className="flex-1 min-w-0 truncate text-xs text-gray-700">{f.title}</span>
                      <ElapsedBadge label={f.elapsed_label} />
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-right">
                <button
                  type="button"
                  data-testid="confirm-all-follow-ups"
                  className="text-sm text-blue-600 border border-blue-600 rounded-md px-3 py-1 hover:bg-blue-50 cursor-pointer whitespace-nowrap"
                  onClick={confirmAllFollowUps}
                >
                  すべて対応済みにする（{doctor_follow_up_total}件）
                </button>
              </div>
            </ActionBlock>
          )}

          {pending_examinations.length > 0 && (
            <ActionBlock
              title="処方提案が未入力の診療"
              description="診察時間が終了したのに処方提案が入力されていない診療です。担当医師に入力を促してください。"
            >
              <ul className={listClass}>
                {pending_examinations.map((e) => (
                  <li key={e.id}>
                    <a href={e.path} target="_blank" rel="noopener" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                      <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0">{e.start_at}</span>
                      <span className="text-xs text-gray-700 whitespace-nowrap shrink-0">{e.doctor_name} 医師</span>
                      <span className="flex-1 min-w-0 truncate text-xs text-gray-700">{e.patient_name ? `${e.patient_name} 様` : '患者情報なし'}</span>
                      <span className="min-w-0 text-[11px] text-gray-500 truncate max-w-[8rem] text-right">{e.department_name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </ActionBlock>
          )}

          {unassigned_consultations.length > 0 && (
            <ActionBlock
              title="未割り当ての医療相談"
              description="担当医師が決まっていない相談です。医師のアサインを調整してください。"
            >
              <ul className={listClass}>
                {unassigned_consultations.map((c) => (
                  <li key={c.id}>
                    <a href={c.path} target="_blank" rel="noopener" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                      <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0">相談 {c.id}</span>
                      <span className="flex-1 min-w-0 truncate text-xs text-gray-700">{c.patient_name ? `${c.patient_name} 様` : '患者情報なし'}</span>
                      <ElapsedBadge label={c.elapsed_label} />
                    </a>
                  </li>
                ))}
              </ul>
            </ActionBlock>
          )}

          {reply_awaiting_consultations.length > 0 && (
            <ActionBlock
              title="医師が未返信の医療相談"
              description="患者の返信に医師が返信しないまま滞留している相談です。担当医師に返信を促してください。"
            >
              <ul className={listClass}>
                {reply_awaiting_consultations.map((c) => (
                  <li key={c.id}>
                    <a href={c.path} target="_blank" rel="noopener" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                      <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0">相談 {c.consultation_id}</span>
                      <span className="text-xs text-gray-700 whitespace-nowrap shrink-0">{c.doctor_name} 医師</span>
                      <span className="flex-1 min-w-0 truncate text-xs text-gray-700">{c.patient_name ? `${c.patient_name} 様` : '患者情報なし'}</span>
                      <ElapsedBadge label={c.elapsed_label} />
                    </a>
                  </li>
                ))}
              </ul>
            </ActionBlock>
          )}
        </div>
      </GroupCard>

      {/* 医師からCSへの引継 (#1191)。CS不在の夜間帯に医師が残した要対応事項を翌朝処理する */}
      <GroupCard header={<GroupHeading title="医師からの引継（要CS対応）" />} tone="attention">
        <p className="text-xs text-gray-500 mb-3">
          CS不在の時間帯（22時以降の夜間枠など）に医師が残した引継です。キャンセル処理・キャンセル料の判断などを対応し、済んだら「対応済みにする」を押してください。
        </p>
        {cs_handovers.length === 0 ? (
          <p className="text-sm text-gray-500 px-1 py-2">未対応の引継はありません。</p>
        ) : (
          <ul className={listClass}>
            {cs_handovers.map((h) => (
              <li key={h.id} className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0">{h.created_label}</span>
                  <span className="text-xs text-gray-700 whitespace-nowrap shrink-0">{h.doctor_name} 医師</span>
                  <span className="min-w-0 truncate text-xs text-gray-700">{h.patient_name ? `${h.patient_name} 様` : '患者情報なし'}</span>
                  <ElapsedBadge label={h.elapsed_label} />
                  <a href={h.path} target="_blank" rel="noopener" className="ml-auto text-sm text-blue-600 hover:underline whitespace-nowrap shrink-0">診療を開く</a>
                  <button
                    type="button"
                    className="text-sm text-blue-600 border border-blue-600 rounded-md px-3 py-1 hover:bg-blue-50 cursor-pointer whitespace-nowrap shrink-0"
                    onClick={() => resolveHandover(h)}
                  >
                    対応済みにする
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-600 whitespace-pre-wrap">{h.memo}</p>
              </li>
            ))}
          </ul>
        )}
      </GroupCard>

      {/* チャット開設前の事前確認 (#1143)。Slackの予約通知ピン留めによるシフト引き継ぎの置き換え */}
      <GroupCard header={<GroupHeading title="予約の事前確認" />}>
        <p className="text-xs text-gray-500 mb-3">
          チャット開設前のチェックが済んでいない予約です。医療保険資格情報・薬局の距離（会員登録住所からの直線距離）・連絡先はバッジで自動判定されます。
          処方できない薬の希望など目視が必要な項目は問診を開いて確認し、済んだら「確認済みにする」を押してください。
          <span className="text-pink-700">ピンク色の行は診療が明日までの予約</span>で、今のシフトで確認が必要です。
        </p>
        {precheck_pending_examinations.length === 0 ? (
          <p className="text-sm text-gray-500 px-1 py-2">未確認の予約はありません。</p>
        ) : (
          <ul className={listClass}>
            {precheck_pending_examinations.map((e) => (
              <li key={e.id} className={e.due_soon ? 'bg-pink-50' : ''}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0">{e.start_at}</span>
                  <span className="min-w-0 truncate text-xs text-gray-700 shrink-0 max-w-[9rem]">
                    {e.patient_name ? `${e.patient_name} 様` : '患者情報なし'}
                  </span>
                  {e.insurance_card_missing && (
                    <span className="text-[11px] font-medium text-red-700 bg-red-100 rounded px-2 py-0.5 whitespace-nowrap shrink-0">保険証未登録</span>
                  )}
                  {e.contact_issues.length > 0 && (
                    <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 whitespace-nowrap shrink-0">
                      不備: {e.contact_issues.join('・')}
                    </span>
                  )}
                  {e.pharmacy_far && (
                    <span className="text-[11px] font-medium text-red-700 bg-red-100 rounded px-2 py-0.5 whitespace-nowrap shrink-0">
                      薬局が遠い（登録住所から{e.pharmacy_distance_km}km）
                    </span>
                  )}
                  <span className="flex-1 min-w-0 truncate text-[11px] text-gray-500 text-right">
                    {e.pharmacy_name}
                    {!e.pharmacy_far && e.pharmacy_distance_km != null && `（登録住所から${e.pharmacy_distance_km}km）`}
                  </span>
                  <a href={e.path} target="_blank" rel="noopener" className="text-sm text-blue-600 hover:underline whitespace-nowrap shrink-0">問診を開く</a>
                  <button
                    type="button"
                    className="text-sm text-blue-600 border border-blue-600 rounded-md px-3 py-1 hover:bg-blue-50 cursor-pointer whitespace-nowrap shrink-0"
                    onClick={() => markPrechecked(e)}
                  >
                    確認済みにする
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {recent_prechecks.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-gray-400 mb-1">最近確認済みにした予約（誤操作はここから取り消せます）</p>
            <ul className="text-xs text-gray-500 space-y-1">
              {recent_prechecks.map((p) => (
                <li key={p.id} className="flex items-center gap-2">
                  <span>{p.checked_at} {p.patient_name ? `${p.patient_name} 様` : '患者情報なし'}（{p.checked_by}）</span>
                  <button type="button" className="text-blue-600 hover:underline cursor-pointer" onClick={() => undoPrecheck(p)}>
                    取り消す
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </GroupCard>

      <GroupCard header={<GroupHeading title="今日の診療の進行状況" />}>
        {insuranceMissingCount > 0 && (
          <p className="text-sm text-red-600 font-medium mb-2">資格情報未登録の予約が {insuranceMissingCount} 件あります。</p>
        )}
        {today_examinations.length === 0 ? (
          <p className="text-sm text-gray-500 px-1 py-2">今日の診療はありません。</p>
        ) : (
          /* 1行=1時刻。同時2枠の運用があるため、同じ時刻の診療があるときだけ2列目に並べる */
          <ul className="divide-y divide-gray-100 bg-white border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
            {groupByStartTime(today_examinations).map((group) => (
              <li key={group.time} className="flex items-start gap-3 px-4 py-2">
                <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0 w-10 pt-2.5">{group.time}</span>
                <div className="grid sm:grid-cols-2 gap-2 flex-1 min-w-0">
                  {group.examinations.map((e) => (
                    <a key={e.id} href={e.path} target="_blank" rel="noopener"
                       className="border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50">
                      <span className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 whitespace-nowrap shrink-0">{e.status_text}</span>
                        <span className="min-w-0 truncate text-xs text-gray-700">
                          {e.patient_name ? `${e.patient_name} 様` : '患者情報なし'}
                        </span>
                        {e.insurance_card_missing && (
                          <span className="text-[11px] font-medium text-red-700 bg-red-100 rounded px-2 py-0.5 whitespace-nowrap shrink-0">保険証未登録</span>
                        )}
                      </span>
                      <span className="flex items-center gap-2 mt-1">
                        <span className="min-w-0 truncate text-[11px] text-gray-500">{e.doctor_name} 医師</span>
                        <span className="min-w-0 truncate text-[11px] text-gray-500 ml-auto text-right">{e.department_name}</span>
                      </span>
                    </a>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </GroupCard>

      <GroupCard header={<GroupHeading title="枠が不足している時間帯" />}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-gray-500">
            需要ルールに対して全医師の空き枠が足りない時間帯です（直近7日）。医師に枠の開放を依頼してください。
          </p>
          <a href={nav_paths.doctor_schedules} className="text-sm text-blue-600 hover:underline whitespace-nowrap">カレンダーで見る</a>
        </div>
        {slot_shortages.length === 0 ? (
          <p className="text-sm text-gray-500 px-1 py-2">現在、枠が不足している時間帯はありません。</p>
        ) : (
          <ul className={listClass}>
            {slot_shortages.map((shortage, i) => (
              <li key={i} className="flex items-center gap-4 px-4 py-2">
                <span className="text-sm font-semibold text-[#1a1f36] whitespace-nowrap">
                  {shortage.date_label} {shortage.time_range}
                </span>
                <span className="text-xs font-medium text-pink-700 bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                  あと{shortage.missing}枠
                </span>
                <a href={shortage.calendar_path} className="ml-auto text-sm text-blue-600 hover:underline whitespace-nowrap">カレンダーへ</a>
              </li>
            ))}
          </ul>
        )}
      </GroupCard>
    </>
  )
}

CsHome.layout = (page: ReactNode) => (
  <PersistentLayout>
    <DashboardLayout>{page}</DashboardLayout>
  </PersistentLayout>
)
